'use client';

import React from 'react';
import { ArrowUpDown } from 'lucide-react';

export interface ClientRecord {
  id: string;
  name: string;
  phone: string;
  email: string;
  dni?: string;
  notes?: string;
  points: number;
  tags: string[];
  totalSpent: number;
  appointmentsCount: number;
  createdAt: string;
  subProfiles?: any[];
  recentAppointments?: any[];
}

interface ClientsTableProps {
  clients: ClientRecord[];
  loading: boolean;
  selectedClientId: string | null;
  onSelectClient: (clientId: string) => void;
  onOpenDetail: (client: ClientRecord) => void;
}

export default function ClientsTable({
  clients,
  loading,
  selectedClientId,
  onSelectClient,
  onOpenDetail
}: ClientsTableProps) {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-3xl p-16 text-center shadow-sm">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-500">Cargando base de clientes...</p>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-3xl p-16 text-center shadow-sm">
        <p className="text-base font-bold text-slate-700">No se encontraron clientes.</p>
        <p className="text-xs text-slate-400 mt-1">Modificá los filtros de búsqueda o creá uno nuevo.</p>
      </div>
    );
  }

  // Generar iniciales y colores pastel
  const getAvatarInfo = (name: string) => {
    const parts = name.trim().split(' ');
    const initials = parts.length > 1 
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();

    const colors = [
      'bg-rose-100 text-rose-700',
      'bg-emerald-100 text-emerald-700',
      'bg-amber-100 text-amber-700',
      'bg-sky-100 text-sky-700',
      'bg-purple-100 text-purple-700'
    ];
    const index = (name.charCodeAt(0) || 0) % colors.length;
    return { initials, colorClass: colors[index] };
  };

  return (
    <div className="bg-white border border-slate-200/90 rounded-3xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs text-slate-600">
          <thead className="bg-slate-50/80 border-b border-slate-200/80 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="py-3 px-4 w-10">
                <input type="checkbox" className="rounded text-emerald-600 focus:ring-emerald-500 border-slate-300" />
              </th>
              <th className="py-3 px-4">
                <div className="flex items-center gap-1 cursor-pointer">
                  <span>Cliente</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-400" />
                </div>
              </th>
              <th className="py-3 px-4">Última visita</th>
              <th className="py-3 px-4 text-center">Visitas</th>
              <th className="py-3 px-4 text-right">Gastado</th>
              <th className="py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-1 cursor-pointer">
                  <span>Puntos</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-400" />
                </div>
              </th>
              <th className="py-3 px-4 text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer">
                  <span>Alta</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-400" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {clients.map((client) => {
              const { initials, colorClass } = getAvatarInfo(client.name);
              const isSelected = selectedClientId === client.id;
              const hasVisits = client.appointmentsCount > 0;
              const createdDate = new Date(client.createdAt).toLocaleDateString('es-AR', {
                day: '2-digit',
                month: 'short',
                year: '2-digit'
              });

              return (
                <tr
                  key={client.id}
                  onClick={() => onOpenDetail(client)}
                  className={`hover:bg-slate-50/70 transition cursor-pointer ${
                    isSelected ? 'bg-emerald-50/30' : ''
                  }`}
                >
                  {/* Checkbox */}
                  <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectClient(client.id)}
                      className="rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                    />
                  </td>

                  {/* Cliente (Avatar + Nombre + Teléfono) */}
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs ${colorClass}`}>
                        {initials}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 hover:text-emerald-800 transition">
                          {client.name}
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                          +54{client.phone}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Última Visita */}
                  <td className="py-3.5 px-4 text-slate-500 font-medium italic">
                    {hasVisits ? '13 oct 26' : 'Aún no vino'}
                  </td>

                  {/* Visitas */}
                  <td className="py-3.5 px-4 text-center font-bold text-slate-800">
                    {hasVisits ? client.appointmentsCount : '·'}
                  </td>

                  {/* Gastado */}
                  <td className="py-3.5 px-4 text-right font-black text-slate-900">
                    {client.totalSpent > 0 ? `$ ${client.totalSpent.toLocaleString('es-AR')}` : '·'}
                  </td>

                  {/* Puntos */}
                  <td className="py-3.5 px-4 text-center font-semibold text-slate-500">
                    {client.points || '0'}
                  </td>

                  {/* Alta */}
                  <td className="py-3.5 px-4 text-right font-mono text-slate-400">
                    {createdDate}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
